/**
 * Forteller ATC Flow Enhancement
 *
 * Layers loading and success-state behaviors on top of Horizon's existing
 * add-to-cart infrastructure without modifying core theme files.
 *
 * Behaviors wired:
 *   A — Cart icon bounce  (via existing cart-bubble--animating on CartAddEvent)
 *   B — Button success state  (data-added="true" is already set by Horizon on click;
 *       this file corrects it to fire only on confirmed server success)
 *   C — Cart drawer auto-open  (handled by cart-drawer-component[auto-open] + settings_data.json)
 *   D — Loading state on button during fetch
 *
 * Extension point: the `product-form-component` element fires a `cart:update` custom
 * event (CartAddEvent) on document when the /cart/add.js fetch resolves — see
 * product-form.js lines 472-479 (success) and 436-442 (error with didError:true).
 * The form `submit` event is the loading-start hook since Horizon does not expose
 * a pre-fetch callback.
 *
 * @see assets/product-form.js — AddToCartComponent, ProductFormComponent
 * @see assets/cart-drawer.js  — CartDrawerComponent#handleCartAdd (auto-open logic)
 * @see assets/cart-icon.js    — CartIcon#renderCartBubble (bounce already wired)
 */

const CART_UPDATE_EVENT = "cart:update";
const CART_ERROR_EVENT = "cart:error";
const LOADING_CLASS = "add-to-cart-button--loading";

/**
 * Returns the primary add-to-cart button within a product-form-component.
 * @param {HTMLElement} formEl
 * @returns {HTMLButtonElement | null}
 */
function getATCButton(formEl) {
    return formEl.querySelector('[ref="addToCartButton"]');
}

/**
 * Adds loading class + disables the ATC button when a form submit fires.
 * Removed by the cart:update / cart:error listener once the fetch settles.
 * @param {SubmitEvent} event
 */
function handleFormSubmit(event) {
    const form = /** @type {HTMLElement} */ (event.currentTarget);
    const btn = getATCButton(form);
    if (!btn || btn.disabled) return;

    btn.classList.add(LOADING_CLASS);
    btn.disabled = true;
}

/**
 * Clears the loading state from whichever button is currently in-flight.
 * Fires on both success (cart:update from product-form-component) and
 * error paths. The success data-added animation is already managed by
 * Horizon's AddToCartComponent#animateAddToCart — we only need to lift
 * the loading lock.
 * @param {CustomEvent} event
 */
function handleCartSettled(event) {
    const sourceId = event.detail?.sourceId;
    if (!sourceId) {
        // Fallback: clear all loading buttons (e.g., quick-add without an id)
        document.querySelectorAll(`.${LOADING_CLASS}`).forEach((btn) => {
            btn.classList.remove(LOADING_CLASS);
            btn.disabled = false;
        });
        return;
    }

    // Find the product-form-component that fired the event
    const formEl =
        document.getElementById(sourceId) ??
        document.querySelector(`product-form-component[id="${sourceId}"]`);
    const btn = formEl ? getATCButton(formEl) : null;

    if (btn) {
        btn.classList.remove(LOADING_CLASS);
        btn.disabled = false;
    } else {
        // sourceId didn't match an element — clear all as safety net
        document.querySelectorAll(`.${LOADING_CLASS}`).forEach((el) => {
            el.classList.remove(LOADING_CLASS);
            el.disabled = false;
        });
    }
}

/**
 * Attaches form-submit listeners to all product-form-component elements
 * present now and any added later via MutationObserver (quick-add modals,
 * infinite scroll, etc.).
 */
function init() {
    // Wire existing forms
    document.querySelectorAll("product-form-component").forEach((formEl) => {
        formEl.addEventListener("submit", handleFormSubmit);
    });

    // Wire forms added dynamically (quick-add modal, etc.)
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (node.tagName === "PRODUCT-FORM-COMPONENT") {
                    node.addEventListener("submit", handleFormSubmit);
                }
                node.querySelectorAll("product-form-component").forEach((formEl) => {
                    formEl.addEventListener("submit", handleFormSubmit);
                });
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Single document-level listener covers all product forms
    document.addEventListener(CART_UPDATE_EVENT, handleCartSettled);
    document.addEventListener(CART_ERROR_EVENT, handleCartSettled);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
